import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CategoryTag, categoryColor } from "@/lib/categoryColors";
import { eur } from "@/lib/constants";
import { useUnsavedGuard } from "@/context/UnsavedChanges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Check, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import DocumentPreview from "@/components/DocumentPreview";
import ConfirmDialog from "@/components/ConfirmDialog";
import { QuoteToolbar, QuoteInfoCard } from "@/components/quote/QuoteParts";

export default function QuoteEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const eventId = sp.get("event");
  const navigate = useNavigate();
  const { org } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [linkedEvent, setLinkedEvent] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [quote, setQuote] = useState(null);
  const [openLine, setOpenLine] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [docPages, setDocPages] = useState([]);
  const [form, setForm] = useState({
    contact_id: sp.get("contact") || "",
    title: "Devis prestation",
    lines: [{ name: "", description: "", qty: 1, price: 0 }],
    tax_rate: org?.tax_rate_default ?? 0,
    deposit_percent: org?.deposit_percent || 30,
    balance_days_before: org?.balance_days_before || 7,
    notes: "",
  });

  const snap = (f) => JSON.stringify({
    contact_id: f.contact_id || null, event_id: f.event_id || null, title: f.title || "",
    lines: (f.lines || []).map((l) => ({ name: l.name || "", description: l.description || "", qty: parseFloat(l.qty) || 0, price: parseFloat(l.price) || 0, line_type: l.line_type || "item", stock_link: l.stock_link || null, parent_uid: l.parent_uid || null, custom_selections: (l.custom_selections || []).map((s) => ({ label: s.label, value: s.value || "" })) })),
    tax_rate: parseFloat(f.tax_rate) || 0, deposit_percent: parseFloat(f.deposit_percent) || 0,
    balance_days_before: parseInt(f.balance_days_before) || 0, notes: f.notes || "",
    opening_page_id: f.opening_page_id || null, closing_page_id: f.closing_page_id || null,
  });
  const savedSnapRef = useRef(null);

  useEffect(() => {
    if (!id && !eventId) { toast.error("Un devis se crée depuis un événement"); navigate("/app/calendrier", { replace: true }); return; }
    api.get("/contacts").then(({ data }) => setContacts(data));
    api.get("/inventory").then(({ data }) => setCatalog(data));
    api.get("/doc-pages").then(({ data }) => setDocPages(data)).catch(() => {});
    if (id) api.get(`/quotes/${id}`).then(({ data }) => { const nf = { ...data, contact_id: data.contact_id || "" }; setQuote(data); setForm(nf); savedSnapRef.current = snap(nf); if (data.event_id) api.get(`/events/${data.event_id}`).then(({ data: ev }) => setLinkedEvent(ev)).catch(() => {}); });
    else if (eventId) api.get(`/events/${eventId}`).then(({ data: ev }) => { setLinkedEvent(ev); setForm((f) => ({ ...f, event_id: eventId, contact_id: ev.contact_id || f.contact_id, title: `Devis — ${ev.title}` })); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagesOf = (kind) => docPages.filter((p) => p.kind === kind);
  const selectPage = (kind, pid) => {
    const p = docPages.find((x) => x.id === pid);
    setForm((f) => ({ ...f,
      [kind === "opening" ? "opening_page_id" : "closing_page_id"]: pid || null,
      [kind === "opening" ? "opening_image" : "closing_image"]: p ? p.image : null,
    }));
  };
  useEffect(() => {
    if (id || !linkedEvent || !docPages.length) return;
    setForm((f) => {
      const upd = { ...f };
      if (!f.opening_page_id) { const p = docPages.find((x) => x.kind === "opening" && x.category === linkedEvent.category); if (p) { upd.opening_page_id = p.id; upd.opening_image = p.image; } }
      if (!f.closing_page_id) { const p = docPages.find((x) => x.kind === "closing" && x.category === linkedEvent.category); if (p) { upd.closing_page_id = p.id; upd.closing_image = p.image; } }
      return upd;
    });
  }, [linkedEvent, docPages, id]);

  const totals = useMemo(() => {
    const subtotal = form.lines.reduce((s, l) => s + (l.qty || 0) * (l.price || 0), 0);
    const tax = subtotal * ((form.tax_rate || 0) / 100);
    const total = subtotal + tax;
    const deposit = total * ((form.deposit_percent || 0) / 100);
    return { subtotal, tax, total, deposit, balance: total - deposit };
  }, [form]);

  const newUid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
  const setLine = (i, k, v) => setForm((f) => {
    const lines = f.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l));
    if (k === "qty") {
      const parent = lines[i];
      if (parent && parent.uid) {
        const pQty = parseFloat(v) || 0;
        return { ...f, lines: lines.map((l) => (l.parent_uid === parent.uid ? { ...l, qty: (l.per_unit || 1) * pQty } : l)) };
      }
    }
    return { ...f, lines };
  });
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { uid: newUid(), name: "", description: "", qty: 1, price: 0, line_type: "item" }] }));
  const addTextLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { uid: newUid(), name: "", description: "", qty: 0, price: 0, stock_link: null, line_type: "text" }] }));
  const removeLine = (i) => setForm((f) => {
    const target = f.lines[i];
    return { ...f, lines: f.lines.filter((l, idx) => idx !== i && !(target && target.uid && l.parent_uid === target.uid)) };
  });
  const buildEssentials = (it, parentUid, parentQty) => (it.essentials || []).map((ess) => {
    const ci = catalog.find((c) => c.id === ess.item_id);
    if (!ci) return null;
    const per = ess.quantity || 1;
    return { uid: newUid(), name: ci.name, description: ci.description || "", qty: per * (parentQty || 1), price: 0, stock_link: ci.type === "materiel" ? ci.id : null, line_type: "essential", parent_uid: parentUid, per_unit: per };
  }).filter(Boolean);
  const packLinesFor = (it) => {
    const out = []; let sum = 0;
    (it.components || []).forEach((comp) => {
      const ci = catalog.find((c) => c.id === comp.item_id);
      if (ci) { sum += ci.price * (comp.quantity || 1); out.push({ uid: newUid(), name: ci.name, description: ci.description || "", qty: comp.quantity || 1, price: ci.price, stock_link: ci.type === "materiel" ? ci.id : null, line_type: "item" }); }
    });
    const diff = (it.price || 0) - sum;
    if (Math.abs(diff) > 0.01) out.push({ uid: newUid(), name: `Remise pack — ${it.name}`, description: "", qty: 1, price: Math.round(diff * 100) / 100, stock_link: null, line_type: "item" });
    return out;
  };
  const withCustom = (it, base, prev) => {
    const defs = it.custom_fields || [];
    if (!defs.length) return base;
    const prevSel = prev && prev.catalog_id === it.id ? prev.custom_selections || [] : [];
    const selections = defs.map((cf) => { const found = prevSel.find((s) => s.label === cf.label); return { label: cf.label, value: found ? found.value : "" }; });
    return { ...base, custom_fields: defs, custom_selections: selections };
  };
  const setLineSelection = (i, k, value) => setForm((f) => ({
    ...f,
    lines: f.lines.map((l, idx) => {
      if (idx !== i) return l;
      const base = l.custom_selections || (l.custom_fields || []).map((cf) => ({ label: cf.label, value: "" }));
      return { ...l, custom_selections: base.map((s, si) => (si === k ? { ...s, value } : s)) };
    }),
  }));
  const addCatalogItem = (it) => {
    if (it.type === "pack") { setForm((f) => ({ ...f, lines: [...f.lines, ...packLinesFor(it)] })); return; }
    const pUid = newUid();
    const parent = withCustom(it, { uid: pUid, name: it.name, description: it.description || "", qty: 1, price: it.price, stock_link: it.type === "materiel" ? it.id : null, catalog_id: it.id, line_type: "item" });
    setForm((f) => ({ ...f, lines: [...f.lines, parent, ...buildEssentials(it, pUid, 1)] }));
  };

  const lineMatches = (q) => {
    const s = (q || "").trim().toLowerCase();
    if (!s) return [];
    return catalog.filter((c) => (c.name || "").toLowerCase().includes(s)).slice(0, 8);
  };
  const pickItem = (i, it) => {
    setForm((f) => {
      if (it.type === "pack") {
        const before = f.lines.slice(0, i); const after = f.lines.slice(i + 1);
        return { ...f, lines: [...before, ...packLinesFor(it), ...after] };
      }
      const current = f.lines[i] || {};
      const pUid = current.uid || newUid();
      const pQty = parseFloat(current.qty) || 1;
      const parent = withCustom(it, { uid: pUid, name: it.name, description: it.description || "", qty: pQty, price: it.price, stock_link: it.type === "materiel" ? it.id : null, catalog_id: it.id, line_type: "item" }, current);
      const before = f.lines.slice(0, i); const after = f.lines.slice(i + 1);
      return { ...f, lines: [...before, parent, ...buildEssentials(it, pUid, pQty), ...after] };
    });
    setOpenLine(null);
  };

  const payload = () => ({
    contact_id: form.contact_id || null, event_id: form.event_id || null, title: form.title,
    lines: form.lines.map((l) => ({ name: l.name, description: l.description || "", qty: parseFloat(l.qty) || 0, price: parseFloat(l.price) || 0, stock_link: l.stock_link || null, catalog_id: l.catalog_id || null, line_type: l.line_type || "item", uid: l.uid || null, parent_uid: l.parent_uid || null, per_unit: l.per_unit ?? null, custom_fields: l.custom_fields || null, custom_selections: l.custom_selections || null })),
    tax_rate: parseFloat(form.tax_rate) || 0, deposit_percent: parseFloat(form.deposit_percent) || 0,
    balance_days_before: parseInt(form.balance_days_before) || 0, notes: form.notes,
    opening_page_id: form.opening_page_id || null, closing_page_id: form.closing_page_id || null,
  });

  const save = async () => {
    if (id) {
      const p = payload();
      const { data } = await api.put(`/quotes/${id}`, p);
      const mism = ["title", "tax_rate", "deposit_percent", "balance_days_before", "notes"].filter((k) => String(p[k] ?? "") !== String(data?.[k] ?? ""));
      if (mism.length) { toast.error(`Enregistrement incomplet : ${mism.join(", ")} non sauvegardé(s). Réessayez.`); return data; }
      const nf = { ...data, contact_id: data.contact_id || "" };
      setQuote(data); setForm(nf); savedSnapRef.current = snap(nf);
      toast.success("Devis enregistré ✓ — vos modifications sont bien sauvegardées", { icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" /> });
      if (data?.reservation_conflicts?.length > 0) {
        toast.warning(`Stock insuffisant, non réservé : ${data.reservation_conflicts.join(", ")}`);
      }
      return data;
    }
    const { data } = await api.post("/quotes", payload());
    toast.success("Devis créé ✓");
    navigate(`/app/devis/${data.id}`);
    return data;
  };

  const send = async () => {
    let q = quote;
    if (!id) { q = await save(); } else { q = await save(); }
    const qid = q?.id || id;
    const { data: sent } = await api.post(`/quotes/${qid}/send`);
    setQuote((prev) => ({ ...(prev || q), status: "envoye" }));
    if (sent?.reservation_conflicts?.length > 0) {
      toast.warning(`Devis envoyé, mais matériel indisponible : ${sent.reservation_conflicts.join(", ")}`);
    } else {
      toast.success("Devis envoyé — lien portail client généré");
    }
  };

  const invoice = async () => { await api.post(`/quotes/${id}/invoice`); const { data } = await api.get(`/quotes/${id}`); setQuote(data); toast.success("Facture générée"); };
  const remove = async () => {
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Document supprimé");
      navigate(quote?.event_id ? `/app/evenements/${quote.event_id}` : "/app/devis");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Suppression impossible"); }
    finally { setDelOpen(false); }
  };
  const duplicate = async () => { const { data } = await api.post(`/quotes/${id}/duplicate`); navigate(`/app/devis/${data.id}`); toast.success("Devis dupliqué"); };
  const copyLink = () => { const url = `${window.location.origin}/portail/${quote.public_token}`; navigator.clipboard.writeText(url); toast.success("Lien portail client copié"); };

  const requestSignature = async () => {
    if (!id) return toast.error("Enregistrez le devis d'abord");
    try {
      const { data } = await api.post(`/quotes/${id}/signature`);
      toast.success("Demande de signature créée — le client peut signer");
      setQuote((p) => ({ ...(p || {}), signature: { status: "ongoing", link: data.signature_link } }));
      if (data.signature_link) window.open(data.signature_link, "_blank", "noopener");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const previewQuote = { ...(quote || {}), ...form, ...totals };
  const contactObj = contacts.find((c) => c.id === form.contact_id) || null;

  const downloadPdf = async () => {
    if (!id) return toast.error("Enregistrez le devis d'abord");
    try {
      const { data } = await api.get(`/quotes/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${quote?.is_invoice ? "facture" : "devis"}-${(quote?.is_invoice ? quote?.invoice_number : quote?.number) || id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  const dirty = !!id && savedSnapRef.current !== null && snap(form) !== savedSnapRef.current;
  useUnsavedGuard(dirty);
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  return (
    <div className="space-y-6">
      {dirty && (
        <div data-testid="quote-unsaved-banner" className="sticky top-0 z-30 flex items-center justify-between gap-3 rounded-xl border border-[#FFD500]/40 bg-[#1a1710] px-4 py-2.5 text-sm text-[#FFD500] shadow-lg">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />Modifications non enregistrées — cliquez sur « Enregistrer » avant de quitter.</span>
          <Button data-testid="quote-unsaved-save" onClick={save} className="h-8 shrink-0 rounded-full bg-[#FF6A00] px-4 text-xs font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1 h-3.5 w-3.5" />Enregistrer</Button>
        </div>
      )}
      <QuoteToolbar id={id} quote={quote} onSave={save} onSend={send} onCopyLink={copyLink} onPdf={downloadPdf}
        onSign={requestSignature} onDuplicate={duplicate} onInvoice={invoice} onDelete={() => setDelOpen(true)} />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Editor */}
        <div className="space-y-4">
          <QuoteInfoCard form={form} setForm={setForm} contacts={contacts} linkedEvent={linkedEvent} pagesOf={pagesOf} onSelectPage={selectPage} />

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold text-white">Lignes de prestation</h3>
              {catalog.length > 0 && (
                <Select onValueChange={(v) => { const it = catalog.find((c) => c.id === v); if (it) addCatalogItem(it); }}>
                  <SelectTrigger className="h-8 w-44 border-zinc-800 bg-zinc-950 text-xs"><SelectValue placeholder="+ Depuis catalogue" /></SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">{catalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.type === "pack" ? "📦 " : ""}{c.name} — {eur(c.price)}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              {form.lines.map((l, i) => (
                l.line_type === "text" ? (
                  <div key={l.uid || i} className="flex items-center gap-2 rounded-xl border border-dashed border-[#FFD500]/40 bg-[#FFD500]/5 p-2" data-testid={`line-text-${i}`}>
                    <span className="shrink-0 rounded-full bg-[#FFD500]/15 px-2 py-0.5 text-[10px] font-medium text-[#FFD500]">Texte</span>
                    <Input data-testid={`line-textinput-${i}`} placeholder="Titre / séparateur / note (ex : — Matériel du vendredi —)" className="flex-1 border-zinc-800 bg-zinc-900 font-medium" value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} />
                    <button onClick={() => removeLine(i)} className="p-2 text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ) : (
                <div key={l.uid || i} className={`flex items-start gap-2 rounded-xl border p-2 ${l.line_type === "essential" ? "border-zinc-800/70 bg-zinc-950/60" : "border-zinc-800 bg-zinc-950"}`}>
                  <div className="flex-1 space-y-1">
                    <div className="flex flex-wrap gap-1">
                      {l.stock_link && <span className="inline-flex items-center gap-1 rounded-full bg-[#FFD500]/10 px-2 py-0.5 text-[10px] font-medium text-[#FFD500]">Réserve le matériel</span>}
                      {(() => { const ci = catalog.find((c) => c.id === (l.catalog_id || l.stock_link)); return ci && categoryColor(org, ci.category) ? <span data-testid={`line-category-${i}`}><CategoryTag org={org} name={ci.category} className="!text-[10px]" /></span> : null; })()}
                      {l.line_type === "essential" && <span className="inline-flex items-center gap-1 rounded-full bg-[#FF6A00]/10 px-2 py-0.5 text-[10px] font-medium text-[#FF6A00]">Indispensable</span>}
                    </div>
                    <div className="relative">
                      <Input data-testid={`line-name-${i}`} placeholder="Désignation" className="border-zinc-800 bg-zinc-900" value={l.name}
                        onChange={(e) => { setLine(i, "name", e.target.value); if (l.line_type !== "essential") setOpenLine(i); }}
                        onFocus={() => { if (l.line_type !== "essential") setOpenLine(i); }}
                        onBlur={() => setTimeout(() => setOpenLine((o) => (o === i ? null : o)), 150)} />
                      {openLine === i && l.line_type !== "essential" && lineMatches(l.name).length > 0 && (
                        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-zinc-800 bg-zinc-900 p-1 shadow-xl" data-testid={`line-suggestions-${i}`}>
                          {lineMatches(l.name).map((c) => (
                            <button key={c.id} type="button" data-testid={`line-opt-${i}-${c.id}`}
                              onMouseDown={(ev) => ev.preventDefault()}
                              onClick={() => pickItem(i, c)}
                              className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800">
                              <span className="truncate">{c.type === "pack" ? "📦 " : c.type === "materiel" ? "🔧 " : "🎧 "}{c.name}</span>
                              <span className="shrink-0 text-xs text-[#FF6A00]">{eur(c.price)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Input placeholder="Description (optionnel)" className="border-zinc-800 bg-zinc-900 text-xs" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} />
                    {(l.custom_fields || []).length > 0 && (
                      <div className="grid gap-1.5 pt-1" data-testid={`line-cf-block-${i}`}>
                        {l.custom_fields.map((cf, k) => (
                          <div key={cf.id || k} className="flex items-center gap-2">
                            <span className="w-28 shrink-0 truncate text-xs text-zinc-500">{cf.label}</span>
                            <Select value={(l.custom_selections?.[k]?.value) || ""} onValueChange={(v) => setLineSelection(i, k, v)}>
                              <SelectTrigger data-testid={`line-cf-${i}-${k}`} className="h-8 flex-1 border-zinc-800 bg-zinc-900 text-xs"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                              <SelectContent className="border-zinc-800 bg-zinc-900">
                                {(cf.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Input type="number" data-testid={`line-qty-${i}`} disabled={l.line_type === "essential"} title={l.line_type === "essential" ? "Calculé automatiquement selon la ligne parente" : ""} className="w-16 border-zinc-800 bg-zinc-900 disabled:opacity-60" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                  <Input type="number" className="w-24 border-zinc-800 bg-zinc-900" value={l.price} onChange={(e) => setLine(i, "price", e.target.value)} />
                  <button onClick={() => removeLine(i)} className="p-2 text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
                )
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Button data-testid="add-line" onClick={() => addLine()} variant="ghost" className="text-[#FF6A00] hover:bg-[#FF6A00]/10"><Plus className="mr-1 h-4 w-4" />Ajouter une ligne</Button>
              <Button data-testid="add-text-line" onClick={() => addTextLine()} variant="ghost" className="text-zinc-400 hover:bg-zinc-800"><Plus className="mr-1 h-4 w-4" />Ligne de texte</Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
            <div><Label className="text-xs text-zinc-400">TVA %</Label><Input type="number" className="mt-1 border-zinc-800 bg-zinc-950" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
            <div><Label className="text-xs text-zinc-400">Acompte %</Label><Input type="number" className="mt-1 border-zinc-800 bg-zinc-950" value={form.deposit_percent} onChange={(e) => setForm({ ...form, deposit_percent: e.target.value })} /></div>
            <div><Label className="text-xs text-zinc-400">Solde J-</Label><Input type="number" className="mt-1 border-zinc-800 bg-zinc-950" value={form.balance_days_before} onChange={(e) => setForm({ ...form, balance_days_before: e.target.value })} /></div>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <DocumentPreview template={org?.document_template} org={org} quote={previewQuote} contact={contactObj} />
        </div>
      </div>

      <ConfirmDialog
        open={delOpen} onOpenChange={setDelOpen}
        title="Supprimer ce document ?"
        description={quote ? `« ${(quote.is_invoice ? quote.invoice_number : quote.number) || ""} » sera définitivement supprimé et ne comptera plus dans vos statistiques. Les réservations de stock associées seront libérées.` : ""}
        onConfirm={remove} testid="del-quote-dialog"
      />
    </div>
  );
}
